import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import { documentDisplayName } from '../../lib/documentName';
import type { DocumentResponse } from '../../types';
import { DocumentStatusBadge } from './DocumentStatusBadge';

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-muted-foreground text-xs">{label}</p>
      <div className="text-sm break-words">{children}</div>
    </div>
  );
}

interface Props {
  document: DocumentResponse | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Detalhe do documento com a metadata já retornada pelo list (sem endpoint extra).
 *
 * Não-modal de propósito: é um painel de contexto (estilo Drive), não um passo
 * obrigatório. Como modal, o overlay `fixed inset-0` bloqueava a sidebar inteira
 * e trocar de coleção com o painel aberto parecia a página ter travado.
 */
export function DocumentDetailSheet({ document, onOpenChange }: Props) {
  return (
    <Sheet open={Boolean(document)} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        showOverlay={false}
        className="w-full sm:max-w-md"
        // Segunda defesa do mesmo problema (bug 16414): sendo não-modal, o
        // painel fecha ao detectar interação fora. Quando ele é aberto a partir
        // do menu ⋮, o fechamento do próprio menu gera esses eventos e o painel
        // se fechava sozinho. Ignora quando o evento nasce dentro de um popper
        // do Radix, que é sempre menu/tooltip, nunca clique deliberado do
        // usuário na página.
        onInteractOutside={(event) => {
          const target = event.target as Element | null;
          if (target?.closest?.('[data-radix-popper-content-wrapper]')) {
            event.preventDefault();
          }
        }}
        onFocusOutside={(event) => event.preventDefault()}
      >
        {/* shrink-0 no header e flex-1 no corpo: sem isso o flex encolhe os
            dois proporcionalmente quando o conteúdo cresce (erro longo de
            indexação, por exemplo) e o título fica espremido em vez de o corpo
            rolar. */}
        <SheetHeader className="shrink-0 border-b pr-12">
          <SheetTitle className="truncate" title={document ? documentDisplayName(document) : ''}>
            {document ? documentDisplayName(document) : ''}
          </SheetTitle>
          <SheetDescription>Detalhes de indexação do documento.</SheetDescription>
        </SheetHeader>

        {document && (
          // p-4 igual ao SheetHeader: com o px-1 anterior o corpo encostava na
          // borda enquanto o título ficava recuado. pr-12 no header evita que o
          // nome passe por baixo do botão de fechar.
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
            <Field label="Status">
              <DocumentStatusBadge status={document.status} />
            </Field>

            {/* Visibilidade saiu daqui junto com o seletor na criação: o
                rag-service não filtra leitura por visibilidade hoje, então
                exibir o campo sugeria uma proteção que não existe. */}
            <Field label="Chunks">
              <span className="tabular-nums">{document.chunk_count ?? '—'}</span>
            </Field>

            {document.status === 'processing' && (
              <div className="grid grid-cols-2 gap-4">
                <Field label="Progresso">
                  <span className="tabular-nums">
                    {document.progress > 0 ? `${Math.round(document.progress * 100)}%` : '—'}
                  </span>
                </Field>
                {document.last_window && <Field label="Janela atual">{document.last_window}</Field>}
              </div>
            )}

            <Field label="Criado em">{formatDateTime(document.created_at)}</Field>

            <Field label="Origem">
              <code className="bg-muted block rounded px-2 py-1 font-mono text-xs break-all">
                {document.source_uri}
              </code>
            </Field>

            {document.status === 'failed' && document.error_message && (
              <Field label="Erro">
                {/* É aqui que o texto técnico completo vive, e mensagem de
                    biblioteca vem com caminho e token longo sem espaço:
                    `break-all` é o que impede o painel de rolar na horizontal
                    (`break-words` sozinho não quebra palavra contínua). */}
                <code className="text-destructive bg-destructive/5 block rounded px-2 py-1 font-mono text-xs break-all whitespace-pre-wrap">
                  {document.error_message}
                </code>
              </Field>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
